// app/screens/professional/OfferHistoryScreen.js
// Professional-side read-only view of all past job offers the user has
// responded to — accepted, rejected/declined, or expired. JobsOffersScreen
// is for live offers awaiting a response; this screen is the historical log.
//
// Data source: /api/data/job_offers filtered by the user's identifiers
// (user_id from AsyncStorage user_profile, then candidate_email as a fallback
// for legacy offers created before user accounts existed). The same source
// JobsOffersScreen uses — no extra backend work required.
//
// Tapping a row navigates to OfferDetails. Status badges + response dates
// make the timeline scannable; the search/filter bar narrows by company or
// position. No Accept/Decline buttons here — those live in OfferDetails for
// the offer_received state, which won't appear in this screen by default.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  ScrollView,
  TouchableOpacity,
  FlatList,
  RefreshControl,
  TextInput,
  ActivityIndicator,
  Image,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Map raw DB status → UI status enum + display metadata
const STATUS_META = {
  accepted:        { label: 'Accepted',  color: '#10b981', bg: '#ecfdf5', icon: 'checkmark-circle' },
  rejected:        { label: 'Declined',  color: '#ef4444', bg: '#fef2f2', icon: 'close-circle'      },
  declined:        { label: 'Declined',  color: '#ef4444', bg: '#fef2f2', icon: 'close-circle'      },
  expired:         { label: 'Expired',   color: '#94a3b8', bg: '#f1f5f9', icon: 'time'              },
  withdrawn:       { label: 'Withdrawn', color: '#f59e0b', bg: '#fef3c7', icon: 'remove-circle'     },
  hired:           { label: 'Hired',     color: '#0e4e96', bg: '#e0f2fe', icon: 'briefcase'         },
  pending:         { label: 'Pending',   color: '#f59e0b', bg: '#fef3c7', icon: 'hourglass'         },
  sent:            { label: 'Pending',   color: '#f59e0b', bg: '#fef3c7', icon: 'hourglass'         },
};

// Statuses considered "history" — they're terminal from the candidate's POV.
// Pending/sent are excluded by default since those belong on JobsOffersScreen.
const HISTORICAL_STATUSES = new Set(['accepted', 'rejected', 'declined', 'expired', 'withdrawn', 'hired']);

const FILTERS = [
  { key: 'all',      label: 'All',       icon: 'apps-outline' },
  { key: 'accepted', label: 'Accepted',  icon: 'checkmark-circle-outline' },
  { key: 'declined', label: 'Declined',  icon: 'close-circle-outline'     },
  { key: 'hired',    label: 'Hired',     icon: 'briefcase-outline'        },
  { key: 'expired',  label: 'Expired',   icon: 'time-outline'             },
];

export default function OfferHistoryScreen({ navigation }) {
  const [myUserId, setMyUserId] = useState(null);
  const [myEmail,  setMyEmail]  = useState(null);
  const [offers,   setOffers]   = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Resolve the professional's identifiers — same logic as JobsOffersScreen
  useEffect(() => {
    AsyncStorage.getItem('user_profile').then(p => {
      if (!p) return;
      try {
        const parsed = JSON.parse(p);
        if (parsed?.id || parsed?.user_id) setMyUserId(parsed.id || parsed.user_id);
        if (parsed?.email) setMyEmail(parsed.email);
      } catch {}
    }).catch(() => {});
  }, []);

  // Fetch past offers — same dual-query pattern as JobsOffersScreen
  // (user_id primary, candidate_email fallback) then filter to historical
  // statuses client-side.
  const loadHistory = useCallback(async () => {
    if (!myUserId && !myEmail) {
      setIsLoading(false);
      return;
    }
    try {
      const queries = [];
      if (myUserId) queries.push(`/api/data/job_offers?user_id=${myUserId}&limit=200`);
      if (myEmail)  queries.push(`/api/data/job_offers?candidate_email=${encodeURIComponent(myEmail)}&limit=200`);

      const results = await Promise.all(queries.map(q => apiFetch(q).catch(() => ({ data: [] }))));
      const seen = new Set();
      const merged = [];
      for (const r of results) {
        const arr = Array.isArray(r) ? r : (r?.data || r?.results || []);
        for (const o of arr) {
          if (!o?.id || seen.has(o.id)) continue;
          seen.add(o.id);
          merged.push(o);
        }
      }

      // Transform to UI shape, then filter to historical statuses
      const ui = merged
        .map(o => {
          const status = (o.status || '').toLowerCase();
          const meta = STATUS_META[status] || STATUS_META.pending;
          const salaryNum = Number(o.offered_salary || o.salary) || 0;
          const cur = o.salary_currency || 'GBP';
          const sym = cur === 'GBP' ? '£' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : '';
          // Use the most relevant date for ordering: response_date when
          // the candidate responded, else offer_date or created_at.
          const respondedAt =
            o.response_date || o.accepted_date || o.declined_date || o.rejected_date || null;
          return {
            id: o.id,
            offerId: o.id,
            jobId: o.job_id,
            applicationId: o.job_application_id || null,
            company:   o.company_name || o.employer_name || '',
            companyLogo: o.company_logo || o.logo || null,
            position:  o.position || o.job_title || 'Position',
            location:  o.location || '',
            salary:    salaryNum ? `${sym}${salaryNum.toLocaleString()}` : '',
            offeredOn: o.offer_date || o.created_at || null,
            respondedAt,
            startDate: o.start_date || null,
            status,
            statusLabel: meta.label,
            statusColor: meta.color,
            statusBg:    meta.bg,
            statusIcon:  meta.icon,
            workType:    o.work_type || '',
            contractType: o.contract_type || '',
            rawOffer: o,
          };
        })
        .filter(o => HISTORICAL_STATUSES.has(o.status))
        .sort((a, b) => new Date(b.respondedAt || b.offeredOn || 0) - new Date(a.respondedAt || a.offeredOn || 0));

      setOffers(ui);
    } catch (e) {
      console.warn('OfferHistory load:', e?.message);
      setOffers([]);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [myUserId, myEmail]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Derive the visible list from filter + search
  const visible = useMemo(() => {
    let arr = offers;
    if (filter !== 'all') {
      arr = arr.filter(o => {
        if (filter === 'declined') return o.status === 'declined' || o.status === 'rejected';
        return o.status === filter;
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(o =>
        (o.company || '').toLowerCase().includes(q) ||
        (o.position || '').toLowerCase().includes(q) ||
        (o.location || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [offers, filter, search]);

  // Summary counts shown at the top
  const stats = useMemo(() => {
    const s = { total: offers.length, accepted: 0, declined: 0, hired: 0, expired: 0 };
    for (const o of offers) {
      if (o.status === 'accepted') s.accepted++;
      else if (o.status === 'rejected' || o.status === 'declined') s.declined++;
      else if (o.status === 'hired') s.hired++;
      else if (o.status === 'expired') s.expired++;
    }
    return s;
  }, [offers]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadHistory();
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  const openOffer = (item) => {
    navigation.navigate('OfferDetails', {
      offerId: item.offerId,
      id: item.offerId,
      jobId: item.jobId,
      offer: item.rawOffer,
    });
  };

  const renderCard = ({ item }) => (
    <TouchableOpacity style={styles.card} onPress={() => openOffer(item)} activeOpacity={0.7}>
      <View style={styles.cardHeader}>
        {item.companyLogo ? (
          <Image source={{ uri: item.companyLogo }} style={styles.logo} />
        ) : (
          <View style={[styles.logo, styles.logoPlaceholder]}>
            <MaterialCommunityIcons name="office-building" size={20} color="#64748b" />
          </View>
        )}
        <View style={styles.cardHeaderText}>
          <Text style={styles.position} numberOfLines={1}>{item.position}</Text>
          <Text style={styles.company} numberOfLines={1}>
            {item.company || 'Company'}{item.location ? ` · ${item.location}` : ''}
          </Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: item.statusBg }]}>
          <Ionicons name={item.statusIcon} size={12} color={item.statusColor} />
          <Text style={[styles.statusBadgeText, { color: item.statusColor }]}>{item.statusLabel}</Text>
        </View>
      </View>

      <View style={styles.cardMeta}>
        {item.salary ? (
          <View style={styles.metaItem}>
            <MaterialCommunityIcons name="currency-gbp" size={14} color="#64748b" />
            <Text style={styles.metaText}>{item.salary}</Text>
          </View>
        ) : null}
        {item.workType ? (
          <View style={styles.metaItem}>
            <Ionicons name="home-outline" size={13} color="#64748b" />
            <Text style={styles.metaText}>{item.workType}</Text>
          </View>
        ) : null}
        {item.contractType ? (
          <View style={styles.metaItem}>
            <Ionicons name="document-text-outline" size={13} color="#64748b" />
            <Text style={styles.metaText}>{item.contractType}</Text>
          </View>
        ) : null}
      </View>

      <View style={styles.cardTimeline}>
        <View style={styles.timelineItem}>
          <Ionicons name="paper-plane-outline" size={12} color="#94a3b8" />
          <Text style={styles.timelineLabel}>Offered</Text>
          <Text style={styles.timelineValue}>{fmtDate(item.offeredOn)}</Text>
        </View>
        <View style={styles.timelineSep} />
        <View style={styles.timelineItem}>
          <Ionicons name="checkmark-done-outline" size={12} color="#94a3b8" />
          <Text style={styles.timelineLabel}>Responded</Text>
          <Text style={styles.timelineValue}>{fmtDate(item.respondedAt)}</Text>
        </View>
        {item.startDate ? (
          <>
            <View style={styles.timelineSep} />
            <View style={styles.timelineItem}>
              <Ionicons name="calendar-outline" size={12} color="#94a3b8" />
              <Text style={styles.timelineLabel}>Start</Text>
              <Text style={styles.timelineValue}>{fmtDate(item.startDate)}</Text>
            </View>
          </>
        ) : null}
      </View>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.safe}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color="#0e4e96" />
          <Text style={styles.loadingText}>Loading offer history…</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>Offer History</Text>
          <Text style={styles.headerSubtitle}>
            {stats.total} total · {stats.accepted} accepted · {stats.declined} declined
          </Text>
        </View>
      </View>

      {/* Search */}
      <View style={styles.searchWrap}>
        <Ionicons name="search-outline" size={18} color="#94a3b8" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by company, position, or location…"
          placeholderTextColor="#94a3b8"
          value={search}
          onChangeText={setSearch}
        />
        {search.length > 0 ? (
          <TouchableOpacity onPress={() => setSearch('')}>
            <Ionicons name="close-circle" size={18} color="#94a3b8" />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Filter chips */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.chips}
      >
        {FILTERS.map(f => {
          const isActive = filter === f.key;
          return (
            <TouchableOpacity
              key={f.key}
              style={[styles.chip, isActive && styles.chipActive]}
              onPress={() => setFilter(f.key)}
            >
              <Ionicons
                name={f.icon}
                size={14}
                color={isActive ? '#fff' : '#475569'}
              />
              <Text style={[styles.chipText, isActive && styles.chipTextActive]}>{f.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* List */}
      <FlatList
        data={visible}
        keyExtractor={(item) => String(item.id)}
        renderItem={renderCard}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} tintColor="#0e4e96" />}
        ListEmptyComponent={() => (
          <View style={styles.empty}>
            <Ionicons name="folder-open-outline" size={56} color="#cbd5e1" />
            <Text style={styles.emptyTitle}>No offer history</Text>
            <Text style={styles.emptyText}>
              {filter !== 'all' || search.trim()
                ? 'No offers match the current filter.'
                : "Once you respond to offers, they'll appear here."}
            </Text>
            {(filter !== 'all' || search.trim()) ? (
              <TouchableOpacity
                style={styles.emptyBtn}
                onPress={() => { setFilter('all'); setSearch(''); }}
              >
                <Text style={styles.emptyBtnText}>Clear filters</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={styles.emptyBtn}
                onPress={() => navigation.navigate('JobsOffersScreen')}
              >
                <Text style={styles.emptyBtnText}>View current offers</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  loadingWrap: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 12, color: '#64748b', fontSize: 14 },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#0e4e96',
    paddingTop: 18,
  },
  headerBtn: { width: 36, height: 36, justifyContent: 'center', alignItems: 'center', marginRight: 8 },
  headerTitle: { fontSize: 18, fontWeight: '700', color: '#fff' },
  headerSubtitle: { fontSize: 12, color: '#bfdbfe', marginTop: 2 },

  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 12,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 10,
    height: 42,
    gap: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  searchInput: { flex: 1, fontSize: 14, color: '#1e293b' },

  chips: { paddingHorizontal: 12, paddingBottom: 8, gap: 8 },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: '#fff',
    borderRadius: 16,
    gap: 6,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginRight: 8,
  },
  chipActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  chipText: { fontSize: 13, color: '#475569', fontWeight: '500' },
  chipTextActive: { color: '#fff', fontWeight: '600' },

  listContent: { padding: 12, paddingTop: 4, paddingBottom: 32 },

  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  logo: { width: 40, height: 40, borderRadius: 8, backgroundColor: '#f1f5f9' },
  logoPlaceholder: { justifyContent: 'center', alignItems: 'center' },
  cardHeaderText: { flex: 1, marginLeft: 10 },
  position: { fontSize: 15, fontWeight: '600', color: '#1e293b' },
  company: { fontSize: 12, color: '#64748b', marginTop: 1 },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  statusBadgeText: { fontSize: 11, fontWeight: '600' },

  cardMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 10 },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  metaText: { fontSize: 12, color: '#475569' },

  cardTimeline: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 8,
  },
  timelineItem: { flex: 1, alignItems: 'center' },
  timelineSep: { width: 1, height: 24, backgroundColor: '#e2e8f0' },
  timelineLabel: { fontSize: 10, color: '#94a3b8', marginTop: 2 },
  timelineValue: { fontSize: 11, color: '#1e293b', fontWeight: '600', marginTop: 1 },

  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60, paddingHorizontal: 32 },
  emptyTitle: { fontSize: 16, fontWeight: '600', color: '#1e293b', marginTop: 12 },
  emptyText: { fontSize: 13, color: '#64748b', textAlign: 'center', marginTop: 6 },
  emptyBtn: {
    marginTop: 16,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#0e4e96',
    borderRadius: 8,
  },
  emptyBtnText: { color: '#fff', fontSize: 14, fontWeight: '600' },
});
